import React, { useState, useEffect } from "react";
import MaterialTable from "material-table";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button, CircularProgress, Switch, FormControlLabel, Typography, Divider,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Chip, Radio, RadioGroup, TextField
} from "@material-ui/core";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomSelect from "components/CustomSelect/CustomSelect";
import CardForms from "components/Card/CardForms";
import Grid from "@material-ui/core/Grid";
import usePayment from "hooks/usePaymenth";
import useMessage from "hooks/useMessage";
import SnackBarWiiba from "components/Snackbar/SnackBarWiiba";
import PaymentsTable from "components/AmortizationTable/PaymentsTable";
import CustomDataPicker from "components/CustomDataPicker/CustomDataPicker";
import CustomCheckBox from "components/CustomCheckBox/CustomCheckBox";
import CustomInputForm from "components/CustomInput/CustomInputForm";
import MoneyInput from "components/CustomInput/MoneyInput";
import { Tooltip, Box } from "@material-ui/core";
import Api from "functions/Api";
import { ValidationResponse } from "functions/validation";

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    fontWeight: 600, marginTop: theme.spacing(2), marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary, textTransform: "uppercase", fontSize: 12, letterSpacing: 1
  },
  divider: { marginBottom: theme.spacing(2) },
  infoGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px",
    backgroundColor: "#f8f9fa", borderRadius: 8, padding: theme.spacing(2),
    marginBottom: theme.spacing(2), border: "1px solid #e0e0e0"
  },
  infoItem: { display: "flex", flexDirection: "column" },
  infoLabel: { fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 },
  infoValue: { fontSize: 15, fontWeight: 600, color: "#222" },
  selectedRow: {
    backgroundColor: "#e3f2fd", borderRadius: 6, padding: "8px 12px",
    marginBottom: theme.spacing(2), border: "1px solid #90caf9",
    display: "flex", justifyContent: "space-between", alignItems: "center"
  },
  montoFijo: {
    backgroundColor: "#e8f5e9", borderRadius: 6, padding: "10px 14px",
    border: "1px solid #a5d6a7", marginBottom: theme.spacing(2),
    display: "flex", justifyContent: "space-between", alignItems: "center"
  }
}));

const fmtMoney = val => "$" + parseFloat(val || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 });

const pagoSchema = (maxMonto) => Yup.object({
  MontoPago: Yup.number().typeError("Solo numeros").positive("Debe ser mayor a 0")
    .max(maxMonto || 9999999, "No puede exceder el saldo pendiente").required("Requerido"),
  MetodoPago: Yup.number().required("Seleccione metodo").oneOf([1, 3], "Invalido")
});

const contractColumns = [
  { title: "Desarrollo", field: "NOMBRE_DESARROLLO" },
  { title: "Manzana", field: "CODIGO" },
  { title: "Lote", field: "LOTE" },
  { title: "No. Contrato", field: "ID_CONTRATO", type: "numeric" }
];

const Cobranza = () => {
  const classes = useStyles();
  const [searchType, setSearchType] = useState("Contrato");
  const [searchValue, setSearchValue] = useState("");
  const [searchError, setSearchError] = useState("");
  const [searchNombre, setSearchNombre] = useState("");
  const [searchApellidoP, setSearchApellidoP] = useState("");
  const [searchApellidoM, setSearchApellidoM] = useState("");
  const [contracts, setContracts] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ejecutaapi] = Api();
  const [customDate, setCustomDate] = useState(false);
  const [parcialidadSeleccionada, setParcialidadSeleccionada] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [modoPago, setModoPago] = useState("mensualidad"); // "mensualidad" | "otro"

  const { opensnack, typesnackbar, message, handleMuestraSnack, handleClosesSnack } = useMessage();
  const {
    handleGetContractInfo, infopayContract, handlepaymenth,
    tblPaymenth, paymentMade, generateReciboPdf, load
  } = usePayment({ handleMuestraSnack });

  // Leer query param ?contrato=XXX al montar para navegación desde otras pantallas
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const contratoParam = params.get("contrato");
    if (contratoParam && parseInt(contratoParam) > 0) {
      setSearchType("Contrato");
      setSearchValue(contratoParam);
      handleGetContractInfo(0, parseInt(contratoParam));
      // Limpiar el query param de la URL sin recargar
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line

  const esEnganche = !!(infopayContract && infopayContract.pendiente);
  const primeraPendiente = tblPaymenth ? tblPaymenth.find(i => i.estatus !== "A") : null;
  const parcialidadActiva = parcialidadSeleccionada || primeraPendiente;

  const saldoPendiente = tblPaymenth
    ? tblPaymenth.filter(i => i.estatus !== "A").reduce((sum, i) => {
        return sum + (parseFloat(i.montoapagar) || 0) + (parseFloat(i.interes) || 0) - (parseFloat(i.anticipado) || 0);
      }, 0)
    : 0;

  const montoSugerido = parcialidadActiva
    ? ((parseFloat(parcialidadActiva.montoapagar) || 0) + (parseFloat(parcialidadActiva.interes) || 0) - (parseFloat(parcialidadActiva.anticipado) || 0)).toFixed(2)
    : "";

  // El monto que se usa en el form depende del modo
  const montoInicial = esEnganche ? montoSugerido : (modoPago === "mensualidad" ? montoSugerido : "");

  const handleBuscar = async () => {
    setSearchError("");
    setLoading(true);
    setParcialidadSeleccionada(null);
    setModoPago("mensualidad");
    const usuario = localStorage.getItem("user");

    if (searchType === "Contrato") {
      const val = searchValue.trim();
      if (!val || isNaN(val) || parseInt(val) <= 0) { setSearchError("Ingrese un número de contrato válido"); setLoading(false); return; }
      await handleGetContractInfo(0, parseInt(val));

    } else if (searchType === "Cliente") {
      const val = searchValue.trim();
      if (!val || isNaN(val) || parseInt(val) <= 0) { setSearchError("Ingrese un ID de cliente válido"); setLoading(false); return; }
      const body = JSON.stringify({ idCliente: parseInt(val), usuario: JSON.parse(usuario).email });
      try {
        const data = await ejecutaapi("obtieneLotesXCliente", body);
        const { result, error } = ValidationResponse(data);
        if (error) { handleMuestraSnack(error, "error"); }
        else if (!result || result.length === 0) { handleMuestraSnack("No se encontraron contratos", "warning"); setContracts([]); }
        else { setContracts(result); handleMuestraSnack(result.length + " contrato(s)", "success"); }
      } catch (_) { handleMuestraSnack("Error al buscar", "error"); }

    } else if (searchType === "Nombre") {
      if (!searchNombre && !searchApellidoP) { setSearchError("Ingrese al menos nombre o apellido"); setLoading(false); return; }
      const body = JSON.stringify({
        idCliente: 0, nombre: searchNombre, apellidoP: searchApellidoP,
        apellidoM: searchApellidoM, claveElector: "", usuario: JSON.parse(usuario).email
      });
      try {
        const data = await ejecutaapi("obtienecliente", body);
        const { result, error } = ValidationResponse(data);
        if (error) { handleMuestraSnack(error, "error"); }
        else if (!result || result.length === 0) { handleMuestraSnack("No se encontraron clientes", "warning"); setContracts([]); }
        else { setClientes(result); handleMuestraSnack(result.length + " cliente(s) encontrado(s)", "success"); }
      } catch (_) { handleMuestraSnack("Error al buscar", "error"); }
    }
    setLoading(false);
  };

  const handleConfirmPago = values => { setPendingValues(values); setConfirmOpen(true); };
  const handleExecutePago = () => { setConfirmOpen(false); handlepaymenth(pendingValues, infopayContract); };

  const infoVisible = !!infopayContract;
  const esLiquidado = infoVisible && infopayContract.liquidado;
  const mostrarFormPago = infoVisible && !esLiquidado;
  const infoFullWidth = esLiquidado;

  return (
    <div>
      <SnackBarWiiba opensnack={opensnack} tipoSnackBar={typesnackbar} mensaje={message} handleClosesSnack={handleClosesSnack} />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar pago</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {pendingValues && (
              <div>
                <div><strong>Monto:</strong> {fmtMoney(pendingValues.MontoPago)}</div>
                <div><strong>Metodo:</strong> {pendingValues.MetodoPago === 1 ? "Efectivo" : "Transferencia"}</div>
                {parcialidadActiva && <div><strong>Parcialidad No.:</strong> {parcialidadActiva.id}</div>}
                {esEnganche && <div style={{ color: "#e65100", marginTop: 8 }}><strong>Este pago activara el contrato automaticamente.</strong></div>}
                {!esEnganche && modoPago === "otro" && parseFloat(pendingValues.MontoPago) < parseFloat(montoSugerido) && (
                  <div style={{ color: "#1565c0", marginTop: 8 }}>Pago parcial: se abonara a la parcialidad actual.</div>
                )}
                {!esEnganche && modoPago === "otro" && parseFloat(pendingValues.MontoPago) > parseFloat(montoSugerido) && (
                  <div style={{ color: "#1565c0", marginTop: 8 }}>Pago mayor a la parcialidad: el excedente se aplicara a las siguientes.</div>
                )}
              </div>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleExecutePago} color="primary" variant="contained">Confirmar</Button>
        </DialogActions>
      </Dialog>

      <CardForms title="Realizar un pago">
        <Typography variant="subtitle2" className={classes.sectionTitle}>Busqueda</Typography>
        <Divider className={classes.divider} />
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item>
            <RadioGroup row value={searchType} onChange={e => { setSearchType(e.target.value); setSearchValue(""); setSearchError(""); setClientes([]); setContracts([]); }}>
              <FormControlLabel value="Contrato" control={<Radio color="primary" />} label="No. Contrato" />
              <FormControlLabel value="Cliente" control={<Radio color="primary" />} label="ID Cliente" />
              <FormControlLabel value="Nombre" control={<Radio color="primary" />} label="Nombre" />
            </RadioGroup>
          </Grid>

          {searchType !== "Nombre" && (
            <Grid item xs={3}>
              <TextField
                label={searchType === "Contrato" ? "Numero de contrato" : "ID de cliente"}
                value={searchValue}
                onChange={e => { setSearchValue(e.target.value.replace(/\D/g, "")); setSearchError(""); }}
                error={!!searchError} helperText={searchError} variant="standard" fullWidth
                inputProps={{ inputMode: "numeric" }}
              />
            </Grid>
          )}

          {searchType === "Nombre" && (
            <>
              <Grid item xs={3}>
                <TextField label="Nombre(s)" value={searchNombre} onChange={e => setSearchNombre(e.target.value)}
                  variant="standard" fullWidth error={!!searchError} helperText={searchError} />
              </Grid>
              <Grid item xs={2}>
                <TextField label="Apellido paterno" value={searchApellidoP} onChange={e => setSearchApellidoP(e.target.value)}
                  variant="standard" fullWidth />
              </Grid>
              <Grid item xs={2}>
                <TextField label="Apellido materno" value={searchApellidoM} onChange={e => setSearchApellidoM(e.target.value)}
                  variant="standard" fullWidth />
              </Grid>
            </>
          )}

          <Grid item>
            <Button color="primary" variant="contained" onClick={handleBuscar} disabled={loading}>
              Buscar {loading && <CircularProgress size={18} style={{ marginLeft: 8 }} />}
            </Button>
          </Grid>
        </Grid>

        {/* Tabla de clientes (modo Nombre) */}
        {searchType === "Nombre" && clientes.length > 0 && (
          <MaterialTable
            title="Clientes encontrados"
            columns={[
              { title: "ID", field: "ID_CLIENTE", type: "numeric", width: "10%" },
              { title: "Nombre", field: "NOMBRES" },
              { title: "Apellido Paterno", field: "APELLIDO_PATERNO" },
              { title: "Apellido Materno", field: "APELLIDO_MATERNO" }
            ]}
            data={clientes}
            style={{ marginTop: 16 }}
            onRowClick={async (_, rowData) => {
              setParcialidadSeleccionada(null);
              setLoading(true);
              const usuario = localStorage.getItem("user");
              const body = JSON.stringify({ idCliente: rowData.ID_CLIENTE, usuario: JSON.parse(usuario).email });
              try {
                const data = await ejecutaapi("obtieneLotesXCliente", body);
                const { result, error } = ValidationResponse(data);
                if (error) { handleMuestraSnack(error, "error"); }
                else if (!result || result.length === 0) { handleMuestraSnack("No se encontraron contratos", "warning"); setContracts([]); }
                else { setContracts(result); handleMuestraSnack(result.length + " contrato(s)", "success"); }
              } catch (_) { handleMuestraSnack("Error al buscar contratos", "error"); }
              setLoading(false);
            }}
            options={{ rowStyle: { cursor: "pointer" } }}
          />
        )}

        {/* Tabla de contratos (modo Cliente o Nombre) */}
        {(searchType === "Cliente" || searchType === "Nombre") && contracts.length > 0 && (
          <MaterialTable title="Contratos del cliente" columns={contractColumns} data={contracts} style={{ marginTop: 16 }}
            onRowClick={async (_, rowData) => { setParcialidadSeleccionada(null); setLoading(true); await handleGetContractInfo(0, rowData.ID_CONTRATO); setLoading(false); }}
            options={{ rowStyle: { cursor: "pointer" } }}
          />
        )}
        {/* INFO + PAGO */}
        {infoVisible && (
          <Grid container spacing={3} style={{ marginTop: 16 }}>
            <Grid item xs={12} sm={infoFullWidth ? 12 : 6}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                Informacion del contrato
                {esLiquidado && <Chip label="LIQUIDADO" size="small" style={{ marginLeft: 8, backgroundColor: "#4caf50", color: "#fff" }} />}
                {esEnganche && <Chip label="PENDIENTE — COBRAR ENGANCHE" size="small" style={{ marginLeft: 8, backgroundColor: "#ff9800", color: "#fff" }} />}
                {!esLiquidado && !esEnganche && infopayContract.estatus_lote === "C" && <Chip label="CREDITO ACTIVO" size="small" style={{ marginLeft: 8, backgroundColor: "#1565c0", color: "#fff" }} />}
              </Typography>
              <Divider className={classes.divider} />
              <div className={classes.infoGrid}>
                <div className={classes.infoItem}>
                  <span className={classes.infoLabel}>Contrato</span>
                  <span className={classes.infoValue}>{infopayContract.ID_CONTRATO || "—"}</span>
                </div>
                <div className={classes.infoItem}>
                  <span className={classes.infoLabel}>Mensualidades</span>
                  <span className={classes.infoValue}>{infopayContract.pmensualidades || "—"}</span>
                </div>
                <div className={classes.infoItem}>
                  <span className={classes.infoLabel}>Costo total</span>
                  <span className={classes.infoValue}>{fmtMoney(infopayContract.pcosto_total)}</span>
                </div>
                <div className={classes.infoItem}>
                  <span className={classes.infoLabel}>Saldo pendiente</span>
                  <span className={classes.infoValue} style={{ color: "#e65100" }}>{fmtMoney(saldoPendiente)}</span>
                </div>
              </div>
              {parcialidadActiva && (
                <div className={classes.selectedRow}>
                  <div>
                    <Typography variant="caption" style={{ color: "#1565c0" }}>
                      {esEnganche ? "Enganche (pago obligatorio para activar contrato)" : "Parcialidad siguiente"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>No. {parcialidadActiva.id}</strong> — Vence: {parcialidadActiva.fechaapagar} — Monto: {fmtMoney(montoSugerido)}
                    </Typography>
                  </div>
                </div>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              {mostrarFormPago && (
              <>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                {esEnganche ? "Cobro del enganche" : "Datos del pago"}
              </Typography>
              <Divider className={classes.divider} />

              {/* Selector de modo de pago — solo para mensualidades, no enganche */}
              {!esEnganche && (
                <RadioGroup row value={modoPago} onChange={e => setModoPago(e.target.value)} style={{ marginBottom: 12 }}>
                  <FormControlLabel value="mensualidad" control={<Radio color="primary" size="small" />}
                    label={<Typography variant="body2">Mensualidad ({fmtMoney(montoSugerido)})</Typography>} />
                  <FormControlLabel value="otro" control={<Radio color="primary" size="small" />}
                    label={<Typography variant="body2">Otro monto</Typography>} />
                </RadioGroup>
              )}

              {/* Enganche: monto fijo */}
              {esEnganche && (
                <div className={classes.montoFijo}>
                  <Typography variant="body2"><strong>Monto del enganche:</strong></Typography>
                  <Typography variant="body1" style={{ fontWeight: 700, color: "#2e7d32" }}>{fmtMoney(montoSugerido)}</Typography>
                </div>
              )}

              {/* Mensualidad fija: mostrar monto sin input */}
              {!esEnganche && modoPago === "mensualidad" && (
                <div className={classes.montoFijo}>
                  <Typography variant="body2"><strong>Monto de la parcialidad:</strong></Typography>
                  <Typography variant="body1" style={{ fontWeight: 700, color: "#2e7d32" }}>{fmtMoney(montoSugerido)}</Typography>
                </div>
              )}

              <Formik
                key={montoInicial + modoPago + String(esEnganche)}
                initialValues={{ MontoPago: montoInicial, MetodoPago: 1, fechapago: "", consecutivo: false }}
                validationSchema={pagoSchema(saldoPendiente)}
                onSubmit={handleConfirmPago}
              >
                {({ errors, touched }) => (
                  <Form>
                    {/* Input de monto solo visible en modo "otro" */}
                    {!esEnganche && modoPago === "otro" && (
                      <>
                        <MoneyInput
                          labelText="Monto de pago"
                          name="MontoPago"
                          error={errors.MontoPago && touched.MontoPago}
                        />
                        <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: 12 }}>
                          Si el monto es menor a la parcialidad, se registra como abono. Si es mayor, se aplica a las siguientes parcialidades.
                        </Typography>
                      </>
                    )}

                    {!esEnganche && (
                      <FormControlLabel
                        control={<Switch checked={customDate} onChange={() => setCustomDate(!customDate)} color="primary" />}
                        label="Fecha personalizada" style={{ marginBottom: 8 }}
                      />
                    )}

                    <CustomSelect label="Metodo de pago" name="MetodoPago">
                      <MenuItem value={1}>Efectivo</MenuItem>
                      <MenuItem value={3}>Transferencia</MenuItem>
                    </CustomSelect>

                    {customDate && !esEnganche && (
                      <>
                        <CustomDataPicker name="fechapago" label="Fecha de pago" InputLabelProps={{ shrink: true }} maxDate={new Date()} />
                        <CustomCheckBox name="consecutivo" />
                      </>
                    )}

                    <Grid container spacing={2} style={{ marginTop: 16 }}>
                      <Grid item>
                        <Button type="submit" color="primary" variant="contained" disabled={load}>
                          <Box position="relative">
                            {esEnganche ? "Cobrar Enganche" : "Generar Pago"}
                            {load && <CircularProgress size={18} style={{ marginLeft: 8 }} />}
                          </Box>
                        </Button>
                      </Grid>
                      {!esEnganche && (
                        <Grid item>
                          <Tooltip title={!paymentMade ? "Realiza un pago primero" : ""} arrow>
                            <span>
                              <Button type="button" color="primary" variant="outlined"
                                disabled={!paymentMade || load}
                                onClick={async () => { const url = await generateReciboPdf(); window.open(url, "_blank"); }}
                              >Generar Recibo</Button>
                            </span>
                          </Tooltip>
                        </Grid>
                      )}
                    </Grid>
                  </Form>
                )}
              </Formik>
              </>
              )}
            </Grid>
          </Grid>
        )}

        {/* TABLA */}
        {tblPaymenth && (
          <div style={{ marginTop: 24 }}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>Tabla de amortizacion</Typography>
            <Divider className={classes.divider} />
            <PaymentsTable data={tblPaymenth} editableDates={customDate} onRowSelect={rowData => setParcialidadSeleccionada(rowData)} />
          </div>
        )}
      </CardForms>
    </div>
  );
};

export default Cobranza;
